import { config } from "../config/config";
import { parseDateTime, yearsDiff } from "shared/src/date.mjs";

/**
 * @typedef {Date | string | null} DateParam
 */

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function dateInput(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return new Intl.DateTimeFormat("fr-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function dateTimeInput(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, -8);
}

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function longDateTime(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return new Intl.DateTimeFormat(config.locale.time, {
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function shortDateTime(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return new Intl.DateTimeFormat(config.locale.time, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function shortMonth(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return new Intl.DateTimeFormat(config.locale.time, {
    month: "short",
  }).format(date);
}

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function date(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return new Intl.DateTimeFormat(config.locale.time).format(date);
}

export function dateRange(from, to) {
  const to2 = parseDateTime(to);
  if (to2 != null) {
    to2.setDate(to2.getDate() - 1);
  }

  return `${date(from)} - ${date(to2)}`;
}

/**
 * @param {DateParam} date
 *
 * @returns {string | null}
 */
export function time24(date) {
  date = parseDateTime(date);

  if (date == null) {
    return null;
  }

  return (
    String(date.getHours()).padStart(2, "0") +
    ":" +
    String(date.getMinutes()).padStart(2, "0")
  );
}

export function minutesToDuration(minutes) {
  if (Math.abs(minutes) < 100) {
    return `${minutes}m`;
  }

  const h = Math.trunc(minutes / 60);
  const m = Math.abs(minutes % 60);

  return `${h}h ${m}m`;
}

export function absoluteMinutesToDuration(minutes) {
  const absoluteMinute = Math.abs(minutes);
  if (absoluteMinute < 100) {
    return `${absoluteMinute}m`;
  }

  const h = Math.trunc(absoluteMinute / 60);
  const m = absoluteMinute % 60;

  return `${h}h ${m}m`;
}

/**
 * @param {number} ms
 *
 * @returns {string}
 */
export function msToTime(ms) {
  const minutesTotal = Math.abs(Math.floor(ms / 1000 / 60));
  const hours = Math.floor(minutesTotal / 60);
  const minutes = Math.floor(minutesTotal % 60);

  const sign = ms < 0 ? "-" : "";

  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * @param {{first_name?: string, last_name?: string}} [obj]
 *
 * @returns {string}
 */
export function fullName(obj) {
  const parts = [obj?.last_name, obj?.first_name].filter((p) => p != null);
  if (parts.length == 0) {
    return null;
  }

  return parts.join(" ");
}

export function address(obj) {
  const parts = [obj?.street, obj?.city, obj?.post_code].filter(
    (p) => p != null,
  );
  if (parts.length === 0) {
    return null;
  }

  return parts.join(", ");
}

export function phone(obj) {
  const parts = [obj?.phone_prefix, obj?.phone].filter((p) => p != null);

  if (parts.length === 0) {
    return null;
  }

  return parts.join(" ");
}

export function correspondenceAddress(obj) {
  const parts = [
    obj?.correspondence_street,
    obj?.correspondence_city,
    obj?.correspondence_post_code,
  ].filter((p) => p != null);
  if (parts.length === 0) {
    return address(obj);
  }

  return parts.join(", ");
}

export function ageFromBirthDate(birthDate) {
  return yearsDiff(parseDateTime(birthDate), new Date());
}

function isValueEmpty(v) {
  if (v == null) return true;

  switch (typeof v) {
    case "string":
      if (v.length === 0) return true;
      break;
    case "object":
      if (Array.isArray(v) && v.length === 0) return true;
      break;
  }

  return false;
}

export function value(v) {
  return isValueEmpty(v) ? "-" : v;
}
